'use client';

import { useMemo } from 'react';

import { LuckyBall, WinnerTicket } from '@/components';
import { cn } from '@/libs';

import { CARTESIAN_MATRIX, getPrizeSequence, SAMPLE_MATRIX } from './utils/matrix';

type ExtractionMethodProps = {
  selectedValues: string[];
  lotteryAwards?: string[][];
  drawDate?: string;
  winningSequence?: string[];
  isApproximation?: boolean;
  className?: string;
};

export const ExtractionMethod = ({
  selectedValues,
  lotteryAwards,
  drawDate,
  winningSequence,
  isApproximation,
  className,
}: ExtractionMethodProps) => {
  const prizeSequence = useMemo(() => {
    const matrix = lotteryAwards ?? SAMPLE_MATRIX;
    return getPrizeSequence(matrix, selectedValues);
  }, [lotteryAwards, selectedValues]);

  const currentMatrix = useMemo(() => lotteryAwards ?? SAMPLE_MATRIX, [lotteryAwards]);

  const hasWinningSequence = !!winningSequence?.length;
  const drawnValue = prizeSequence.join('');
  const winnerValue = hasWinningSequence ? winningSequence!.join('') : drawnValue;
  const showBothSequences =
    !!lotteryAwards && hasWinningSequence && isApproximation === true;
  const showFinishedSingle =
    !!lotteryAwards && hasWinningSequence && isApproximation !== true;
  const showSample = !showBothSequences && !showFinishedSingle;

  return (
    <div className={cn('w-full md:w-96 h-auto p-2 bg-primary/15 rounded-xl', className)}>
      {drawDate && (
        <div className="flex flex-col items-center w-full py-2 rounded-lg bg-foreground/10">
          <span className="text-sm font-bold text-foreground">
            Resultado Loteria Federal:
          </span>
          <span className="text-xs font-semibold text-textColor-white">
            {new Date(drawDate).toLocaleDateString('pt-BR')}
          </span>
        </div>
      )}

      <div className="w-full flex flex-col mt-4 gap-2">
        {currentMatrix.map((row, rowIndex) => (
          <div key={rowIndex} className="flex items-center justify-between space-y-0">
            <div className="flex flex-col items-start justify-around w-[59px]">
              <span className="mr-1.5 text-xs font-bold text-nowrap text-foreground">
                {`${rowIndex + 1}° Sorteio`}
              </span>
              <span className="mr-1.5 text-2xs font-bold text-nowrap text-foreground/60 text-ellipsis line-clamp-2">
                {`(${rowIndex + 1}° Prêmio da Federal)`}
              </span>
            </div>

            <div className="flex gap-1 pb-4 md:pb-0">
              <LuckyBall number="0" disabled={true} />

              {row.map((element, cellIndex) => {
                const cartesianValue = CARTESIAN_MATRIX[rowIndex][cellIndex];
                const isSelected = selectedValues.includes(cartesianValue);
                const selectedIndex = selectedValues.indexOf(cartesianValue);

                return (
                  <LuckyBall
                    key={cellIndex}
                    number={element}
                    superNumber={
                      isSelected ? (selectedIndex + 1).toString() + 'º' : undefined
                    }
                    highlight={isSelected}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-background-variant w-full mt-4 p-2 text-center flex justify-center flex-col items-center">
        {showBothSequences && (
          <>
            <span
              id="extraction-method-drawn-sequence-title"
              className="font-bold text-sm md:text-base mb-2"
            >
              Sequência sorteada:
            </span>
            <WinnerTicket
              value={drawnValue}
              className="w-2/3 text-base grayscale opacity-70"
            />
            <span
              id="extraction-method-winning-sequence-title"
              className="font-bold text-sm md:text-base mt-3 mb-2 leading-tight"
            >
              Sequência ganhadora
              <br />
              por aproximação:
            </span>
            <WinnerTicket value={winnerValue} className="w-2/3 text-base" />
          </>
        )}

        {showFinishedSingle && (
          <>
            <span
              id="extraction-method-winning-sequence-title"
              className="font-bold text-sm md:text-base mb-2"
            >
              Sequência ganhadora:
            </span>
            <WinnerTicket value={winnerValue} className="w-2/3 text-base" />
          </>
        )}

        {showSample && (
          <>
            <span id="extraction-method-winning-sequence-text-1" className="text-sm">
              Para formar o número, observe as sequências sorteadas pela Loteria Federal e
              siga a ordem estabelecida, como no exemplo acima.
            </span>
            <span
              id="extraction-method-winning-sequence-text-2"
              className="font-bold text-sm my-2"
            >
              No exemplo acima, o número sorteado foi:
            </span>
            <WinnerTicket
              value={prizeSequence.slice(0, 6).join('')}
              className="w-2/3 text-base"
            />
          </>
        )}
      </div>
    </div>
  );
};
